import { Document, Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import {
  Language,
  SALUTATIONS,
  LETTER_TITLES,
  PARAGRAPH_2,
  PARAGRAPH_3,
  PARAGRAPH_5,
  CLOSING_FORMULAS,
  ATTENDANCE_CODE_LABELS,
  ATTENDANCE_TYPE_LABELS,
} from "./types";

const MONTHS: Record<Language, string[]> = {
  fr: ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"],
  en: ["January","February","March","April","May","June","July","August","September","October","November","December"],
  pt: ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"],
};

function formatDate(lang: Language): string {
  const now = new Date();
  const day = now.getDate();
  const month = MONTHS[lang][now.getMonth()];
  const year = now.getFullYear();
  if (lang === "fr") return `Abuja, le ${day} ${month} ${year}`;
  return `Abuja, ${day} ${month} ${year}`;
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 108,
    paddingBottom: 72,
    paddingHorizontal: 72,
    fontSize: 11,
    lineHeight: 1.5,
    fontFamily: "Helvetica",
  },
  bold: { fontFamily: "Helvetica-Bold" },
  journalistBlock: { marginBottom: 20 },
  dateRow: { textAlign: "right", marginBottom: 28 },
  salutation: { marginBottom: 14 },
  title: { fontFamily: "Helvetica-Bold", fontSize: 12, marginBottom: 14 },
  paragraph: { textAlign: "justify", marginBottom: 14 },
  codeBox: {
    marginBottom: 14,
    padding: 8,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    backgroundColor: "#F7F7F7",
  },
  codeValue: { fontFamily: "Courier-Bold", fontSize: 12, color: "#006B3C" },
  closing: { marginBottom: 60 },
  signatory: { textAlign: "right" },
});

interface LetterPdfDocumentProps {
  language: Language;
  firstName: string;
  lastName: string;
  outletName: string;
  gender: string;
  commsEmail: string;
  signedByName: string;
  signedByTitle: string;
  eventName?: string;
  eventDates?: string;
  eventVenue?: string;
  attendanceCode?: string;
  attendanceType?: "physical" | "online";
}

export function LetterPdfDocument({
  language,
  firstName,
  lastName,
  outletName,
  gender,
  commsEmail,
  signedByName,
  signedByTitle,
  eventName,
  eventDates,
  eventVenue,
  attendanceCode,
  attendanceType,
}: LetterPdfDocumentProps) {
  const salutation =
    SALUTATIONS[language][gender] ||
    (language === "fr" ? "Cher(e)" : language === "en" ? "Dear" : "Caro(a)");

  const hasEvent = !!(eventName && eventDates && eventVenue);

  const paragraph1 = (() => {
    if (language === "fr") {
      return hasEvent
        ? `Le Parlement de la CEDEAO a le plaisir de vous convier pour la couverture médiatique de ${eventName} prévue du ${eventDates} à ${eventVenue}.`
        : "Le Parlement de la CEDEAO a le plaisir de vous convier pour la couverture médiatique de ses activités.";
    }
    if (language === "en") {
      return hasEvent
        ? `The ECOWAS Parliament is pleased to invite you to provide media coverage of ${eventName} scheduled from ${eventDates} at ${eventVenue}.`
        : "The ECOWAS Parliament is pleased to invite you to provide media coverage of its activities.";
    }
    return hasEvent
      ? `O Parlamento da CEDEAO tem o prazer de convidá-lo(a) para a cobertura mediática de ${eventName} prevista de ${eventDates} em ${eventVenue}.`
      : "O Parlamento da CEDEAO tem o prazer de convidá-lo(a) para a cobertura mediática das suas actividades.";
  })();

  const paragraph4 = (() => {
    if (language === "fr")
      return `Dans l'espoir que vous honorerez la présente invitation, nous vous saurons gré de confirmer dans les meilleurs délais votre participation à l'adresse suivante : ${commsEmail}, afin que toutes les dispositions relatives à votre voyage (billets d'avion) soient prises dans l'immédiat.`;
    if (language === "en")
      return `We hope you will honour this invitation and kindly request that you confirm your participation as soon as possible at the following address: ${commsEmail}, so that all travel arrangements (flight tickets) can be made immediately.`;
    return `Na esperança de que honre o presente convite, agradecemos que confirme com a maior brevidade possível a sua participação no seguinte endereço: ${commsEmail}, para que todas as disposições relativas à sua viagem (passagens aéreas) sejam tomadas de imediato.`;
  })();

  const closingFormula = CLOSING_FORMULAS[language].replace("{salutation}", salutation);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.journalistBlock}>
          <Text style={styles.bold}>{firstName} {lastName}</Text>
          {outletName && <Text style={styles.bold}>{outletName}</Text>}
        </View>

        <Text style={styles.dateRow}>{formatDate(language)}</Text>

        <Text style={styles.salutation}>{salutation},</Text>

        <Text style={styles.title}>{LETTER_TITLES[language]}</Text>

        <Text style={styles.paragraph}>{paragraph1}</Text>
        <Text style={styles.paragraph}>{PARAGRAPH_2[language]}</Text>
        <Text style={styles.paragraph}>{PARAGRAPH_3[language]}</Text>
        <Text style={styles.paragraph}>{paragraph4}</Text>

        {attendanceCode && attendanceType && (
          <View style={styles.codeBox}>
            <Text>
              <Text style={styles.bold}>{ATTENDANCE_CODE_LABELS[language]} </Text>
              <Text style={styles.codeValue}>{attendanceCode}</Text>
              {"  "}
              {ATTENDANCE_TYPE_LABELS[language][attendanceType]}
            </Text>
          </View>
        )}

        <Text style={styles.paragraph}>{PARAGRAPH_5[language]}</Text>

        <Text style={styles.closing}>{closingFormula}</Text>

        <View style={styles.signatory}>
          <Text style={styles.bold}>{signedByName}</Text>
          <Text>{signedByTitle}</Text>
        </View>
      </Page>
    </Document>
  );
}
